import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod' 
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search, AlertTriangle } from "lucide-react";

interface JobItem {
  id: string;
  type: string;
  status: string;
  creditsUsed: number;
  executionTime: number | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  user: { id: string; email: string; name: string };
  canvas: { uuid: string; title: string };
}

interface JobStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  totalCreditsUsed: number;
  avgExecutionTime: number;
  failureRate: number;
  jobsToday: number;
  jobsThisWeek: number;
  stuckJobs: number;
}

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "text-green-600 border-green-600/30",
  FAILED: "text-red-600 border-red-600/30",
  RUNNING: "text-yellow-600 border-yellow-600/30",
  PENDING: "text-gray-500 border-gray-500/30",
  CANCELED: "text-gray-400 border-gray-400/30",
};

const STATUSES = ["PENDING", "RUNNING", "COMPLETED", "FAILED", "CANCELED"];

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const jobsSearchSchema = z.object({
  search: z.string().optional(),
  status: z.string().optional(),
  page: z.coerce.number().optional(),
})

export const Route = createFileRoute('/dashboard/jobs/')({
  validateSearch: jobsSearchSchema,
  component: JobsListPage,
})

function JobsListPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const [jobs, setJobs] = React.useState<JobItem[]>([]);
  const [stats, setStats] = React.useState<JobStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState(searchParams.search || "");
  const [statusFilter, setStatusFilter] = React.useState(searchParams.status || "");
  const [page, setPage] = React.useState(searchParams.page || 1);
  const limit = 20;

  const fetchJobs = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/jobs?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setJobs(json.data.jobs);
      setTotal(json.data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search, statusFilter, page]);

  React.useEffect(() => { fetchJobs(); }, [fetchJobs]);
  React.useEffect(() => {
    fetch(`${API_ADMIN_BASE_URL}/api/admin/jobs/stats`, { credentials: "include" })
      .then((r) => r.json()).then((j) => setStats(j.data)).catch(() => {});
  }, []);
  React.useEffect(() => { document.title = "Jobs - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search, statusFilter]);

  // Auto-refresh every 30s
  React.useEffect(() => {
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Jobs</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Jobs</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} jobs · Auto-refreshing every 30s</p>
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Total</span><span className="text-lg font-semibold">{stats.total.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Running</span><span className="text-lg font-semibold text-yellow-600">{(stats.byStatus.RUNNING || 0).toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Today</span><span className="text-lg font-semibold">{stats.jobsToday.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Credits Used</span><span className="text-lg font-semibold">{stats.totalCreditsUsed.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Failure Rate</span><span className="text-lg font-semibold">{stats.failureRate}%</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Avg Duration</span><span className="text-lg font-semibold">{stats.avgExecutionTime}s</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Stuck</span><span className={`text-lg font-semibold ${stats.stuckJobs > 0 ? "text-red-500" : ""}`}>{stats.stuckJobs}</span></div>
            </div>
          )}
        </div>
      </header>

      {/* Stuck alert */}
      {stats && stats.stuckJobs > 0 && (
        <div className="flex items-center gap-2 bg-red-500/10 border-b border-red-500/30 px-4 py-2 md:px-6">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-500 font-medium">{stats.stuckJobs} job(s) stuck (RUNNING &gt; 10 min)</span>
        </div>
      )}

      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input placeholder="Search ID, email, or node..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge variant={statusFilter === "" ? "default" : "outline"} className="cursor-pointer" onClick={() => setStatusFilter("")}>All</Badge>
            {STATUSES.map((s) => (
              <Badge key={s} variant={statusFilter === s ? "default" : "outline"} className={`cursor-pointer ${statusFilter !== s ? STATUS_COLORS[s] : ""}`} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}>{s}</Badge>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Canvas</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : jobs.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No jobs found</TableCell></TableRow>
                ) : jobs.map((j) => (
                  <TableRow key={j.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate({ to: `/dashboard/jobs/${j.id}` })}>
                    <TableCell className="font-mono text-xs">{j.id.slice(0, 8)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{j.type.replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${STATUS_COLORS[j.status] || ""}`}>{j.status}</Badge></TableCell>
                    <TableCell className="text-sm">{j.user.email}</TableCell>
                    <TableCell className="text-sm truncate max-w-[150px]">{j.canvas.title}</TableCell>
                    <TableCell className="text-right text-sm">{j.creditsUsed}</TableCell>
                    <TableCell className="text-right text-sm">{j.executionTime ? `${j.executionTime}s` : "-"}</TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{formatDate(j.createdAt)}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number;
                if (totalPages <= 5) pn = i + 1; else if (page <= 3) pn = i + 1; else if (page >= totalPages - 2) pn = totalPages - 4 + i; else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}
